import { Box, Button, Typography } from '@mui/material';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import { ICON_SIZE_HEADER } from '../../../theme/tokens';
import {
  formatChangedAt,
  staleEditTheirs,
  staleEditWho,
  type StaleEdit,
} from '../../../utils/staleEdit';

const GAP_PX = 10;

export interface StaleEditBannerProps {
  who: StaleEdit;
  /** Labels of the fields both people changed, as the form names them. */
  clashLabels: string[];
  /** Takes the other version wholesale. Omitted where the surface has no such action. */
  onDiscardMine?: () => void;
  /** Tighter padding and type, for dialogs and phones. */
  compact?: boolean;
}

/**
 * Board C's banner: who saved first and when, that their changes are now in the
 * form, and which fields need a look. It never says "reload": nothing the
 * reader typed was lost, and Save again sends the new version.
 *
 * The tone follows the content: warn while fields clash, info (a sync mark)
 * when the other change merged cleanly and only needs saving over.
 */
export function StaleEditBanner({ who, clashLabels, onDiscardMine, compact }: StaleEditBannerProps) {
  const when = formatChangedAt(who.changedAt);
  const clashing = clashLabels.length > 0;
  const subject = who.byYou ? 'You saved this in another tab' : `${staleEditWho(who)} saved this`;
  const title = `${subject} at ${when}. ${who.byYou ? 'Those' : 'Their'} changes are now in the form.`;
  const body = clashing
    ? `${who.byYou ? 'Both saves' : 'You both'} changed ${listOf(clashLabels)}: keep yours or use ${staleEditTheirs(who)}, then save again.`
    : 'Your changes are kept. Save again to keep them.';
  const Icon = clashing ? ReportProblemOutlinedIcon : SyncIcon;
  const fontSize = compact ? '0.8125rem' : '0.875rem';

  return (
    <Box
      role="alert"
      sx={{
        display: 'flex',
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        columnGap: `${GAP_PX}px`,
        rowGap: 0.5,
        alignItems: 'flex-start',
        p: compact ? 1.25 : 1.75,
        borderRadius: 1,
        bgcolor: clashing ? 'statusSoft.warn' : 'statusSoft.info',
      }}
    >
      {/* The tone rides the icon; the words stay in the text colours, as in
          ReportLoadError, because status ink on its own tint is under 4.5:1. */}
      <Icon
        aria-hidden
        sx={{
          fontSize: ICON_SIZE_HEADER,
          mt: 0.25,
          flexShrink: 0,
          color: clashing ? 'statusInk.warn' : 'statusInk.info',
        }}
      />
      {/* On a phone the text takes the whole row beside the icon, so the button wraps beneath it. */}
      <Box sx={{ minWidth: 0, flex: { xs: `1 1 calc(100% - ${ICON_SIZE_HEADER + GAP_PX}px)`, sm: 1 } }}>
        <Typography sx={{ fontSize, fontWeight: 600, color: 'text.primary' }}>{title}</Typography>
        <Typography sx={{ fontSize, color: 'text.secondary', mt: 0.25 }}>{body}</Typography>
      </Box>
      {onDiscardMine && (
        <Button
          size="small"
          variant="text"
          onClick={onDiscardMine}
          sx={{
            flexShrink: 0,
            alignSelf: { xs: 'flex-start', sm: 'center' },
            // Under the text on a phone, in line with it, not under the icon.
            ml: { xs: `${ICON_SIZE_HEADER + GAP_PX}px`, sm: 0 },
          }}
        >
          Discard mine
        </Button>
      )}
    </Box>
  );
}

function listOf(labels: string[]): string {
  if (labels.length <= 1) return labels.join('');
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

export default StaleEditBanner;
